/* What a margin note IS — asked once, in one place.
 *
 * THIS EXISTS BECAUSE THE ANSWER WAS WRITTEN FOUR TIMES AND THREE WERE WRONG.
 * `MarginNote.vue` asked `kind === 'voice' || kind === 'video'`, which is
 * right. `NoteRow.vue`, `RecipeSheetPage.vue` and `CapturePage.vue` each
 * asked `kind !== 'written'`, which is not: a PHOTOGRAPH is not written, so
 * every one of them treated a picture as something you could hear.
 *
 * What that cost, in the built app: a photo wore a play triangle it could not
 * honour, its menu offered "Play it", and — worst — the delete confirmation
 * counted it and told someone "the one note in Dad's voice goes with it"
 * about a picture. The single dialog in this product that must be exactly
 * true was making a false claim about a dead person's voice, to the one
 * reader with no way to check it.
 *
 * A duplicated predicate does not drift all at once; it drifts one call site
 * at a time, and the copy that matters most is the one nobody re-reads.
 */
import { isDurableMedia, isDirectUrl } from './audio-store.js';

/** The kinds a note can be. 'written' has no media; the rest do. */
export const VOICE_KINDS = ['voice', 'video'];

/**
 * Is there something to HEAR — the only question the play controls should ask.
 *
 * Both halves matter. A photo is not audible however durable its file is, and
 * a voice note whose recording has been evicted is not audible either: a play
 * triangle on it promises a voice and delivers a dialog of bad news.
 */
export const isAudible = (note) =>
  !!note && VOICE_KINDS.includes(note.kind) && isDurableMedia(note.mediaUrl);

/** A picture that can actually be shown. */
export const isPhoto = (note) =>
  !!note && note.kind === 'photo' && isDirectUrl(note.mediaUrl);

/** Recorded as a voice, but the audio is not on this device any more. */
export const isSilenced = (note) =>
  !!note && VOICE_KINDS.includes(note.kind) && !isDurableMedia(note.mediaUrl);

/** mm:ss. Durations here are seconds, and always short. */
export const clock = (seconds) => {
  const n = Math.max(0, Math.round(Number(seconds) || 0));
  return `${Math.floor(n / 60)}:${String(n % 60).padStart(2, '0')}`;
};

/**
 * What the note is, in one line, in the order a person would say it.
 *
 * A written note is its own words, because there is nothing more useful to
 * put there. A voice note is whose voice and how long — which is what tells
 * two recordings on one step apart, and the reason a row of identical discs
 * could not.
 */
export function noteLabel(note, cookName = '') {
  if (!note) return '';
  const who = cookName || 'Their voice';
  if (isAudible(note)) {
    return note.durationSeconds ? `${who} · ${clock(note.durationSeconds)}` : who;
  }
  if (note.kind === 'photo') return note.body || `A photograph from ${cookName || 'them'}`;
  if (note.kind === 'written') return note.body || 'Note';
  return note.body || 'No recording saved';
}
